import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { t, getUTCTimeStamp, triggerTrackingUrl } from '@insight/toolkit-utils';
import Button from '../Button/Button';
import Icon from '../Icon/Icon';
import Modal from '../Modal/Modal';
import ButtonGroup from '../ButtonGroup/ButtonGroup';
import AddToCartProtection from './AddToCartProtection';
import { Skeleton } from '../Skeleton/Skeleton';

const AddToCartWarrantyModal = ({
  protection,
  thirdPartyProtection,
  products,
  addToCart,
  setCurrentModal,
  selectedWarranty,
  setSelectedWarranty,
  showPrice,
  categoryType,
  showVAT,
  asyncPrice,
  richRelevanceSSEComplete,
  isIPSUserWithContract,
  carriersNotAvailable,
  selectedCarrier,
  setIsProtectionClick,
}) => {
  const [addToCartState, setAddToCartState] = useState({
    loading: false,
    fulfilled: false,
  });

  const switchModal = () => {
    setSelectedWarranty({});
    setCurrentModal('accessories');
    setIsProtectionClick(true);
  };

  const priceText = 'Your price: ';
  const defaultCheckedWarrantyId =
    protection?.length > 0
      ? protection[0]?.materialId
      : thirdPartyProtection[0]?.materialId;

  // should be changed, refer to <productdetailtabresources>
  const checkedProtection = selectedWarranty
    ? selectedWarranty?.materialId
    : protection[0]?.materialId || thirdPartyProtection[0]?.materialId;

  // Add warranty Items to cart
  const addItemToCart = async () => {
    try {
      setAddToCartState({
        loading: true,
        fulfilled: false,
      });
      // RR tracking event
      triggerTrackingUrl(selectedWarranty?.clickTrackingURL);
      // Add resource to cart
      await addToCart(
        [
          {
            clientBrowserDate: getUTCTimeStamp(),
            materialID: checkedProtection,
            quantity: 1,
            contractID: selectedWarranty?.contractID,
          },
        ],
        categoryType
      );
      setAddToCartState({ loading: false, fulfilled: true });
      setCurrentModal('accessories');
    } catch (err) {
      console.error(err);
    }
  };

  // Get selected warranty
  const setWarranty = (warranty, asyncItem) => {
    setSelectedWarranty({
      ...warranty,
      categoryType: 'protection-plan',
      contractID: asyncItem?.contractNumber,
      callForPrice: isIPSUserWithContract ? asyncItem?.callForPrice : false,
      hasAvilablePrice: isIPSUserWithContract ? !!asyncItem : true,
    });
  };

  // set defaultwarranty
  // const defaultWarranty = protection?.prodList[0]?.materialId || thirdPartyProtection?.prodList[0]?.materialId;
  const renderManufactorWarranty = () =>
    protection?.length ? (
      <>
        <h4>{t('Manufacturer protection')}</h4>
        {protection.map((warranty, index) => (
          <AddToCartProtection
            warranty={warranty}
            setWarranty={setWarranty}
            priceText={priceText}
            selectedWarranty={selectedWarranty}
            defaultCheckedWarrantyId={defaultCheckedWarrantyId}
            showPrice={showPrice}
            showVAT={showVAT}
            asyncPrice={asyncPrice}
            richRelevanceSSEComplete={richRelevanceSSEComplete}
            isIPSUserWithContract={isIPSUserWithContract}
            index={index}
          />
        ))}
      </>
    ) : null;

  const render3rdPartyWarranty = () =>
    thirdPartyProtection?.length ? (
      <>
        <h4>{t('Other protection')}</h4>
        {thirdPartyProtection.map((warranty, index) => (
          <AddToCartProtection
            warranty={warranty}
            setWarranty={setWarranty}
            priceText={priceText}
            selectedWarranty={selectedWarranty}
            defaultCheckedWarrantyId={defaultCheckedWarrantyId}
            showDesc={false}
            showPrice={showPrice}
            showVAT={showVAT}
            index={index}
          />
        ))}
      </>
    ) : null;

  const backToCarriers = () => setCurrentModal('carrier');
  const backToCarriersLink = !carriersNotAvailable ? (
    <div>
      <span className="back-to-carriers" onClick={backToCarriers}>
        <Icon
          icon="arrow-left"
          name="arrow-left"
          aria-label={t('Back to carriers')}
          title="Back to carriers"
        />
        {t('Back to carriers')}
      </span>
    </div>
  ) : null;
  // if (!protection?.length && !thirdPartyProtection?.length) return null
  return (
    <Modal className="c-add-item-to-cart" isOpen size="medium">
      <div className="c-add-item-to-cart__warranty">
        <Modal.Header>
          <div className="o-grid">
            <Icon
              icon="checkmark-circled"
              className="c-form__check-icon"
              type="success"
              title="Added to cart"
            />
            <div>
              <h2 className="o-grid o-grid--center">{t('Added to cart')}</h2>
              <p>
                {products[0]?.description ||
                  products[0]?.descriptions?.shortDescription}
              </p>
            </div>
          </div>
        </Modal.Header>
        <Modal.Body
          id="added-to-cart-warranty-modal"
          data-testid="added-to-cart-warranty-modal"
        >
          {backToCarriersLink}
          <h3>{t('Protect your purchase')}</h3>
          {!richRelevanceSSEComplete && (
            <p>
              {t(
                'Insight recommends adding an extended warranty to your purchase and secure the longevity of your product. Please wait while loading...'
              )}
            </p>
          )}
          {!richRelevanceSSEComplete && (
            <div>
              <Skeleton height="50px" width="100%" count={4} />
            </div>
          )}

          {richRelevanceSSEComplete && protection?.length === 0 && (
            <p>
              Unfortunately, we currently do not have recommended protection
              options available for this product.
            </p>
          )}

          {richRelevanceSSEComplete && protection?.length > 0 && (
            <p>
              {t(
                "Insight recommends adding an extended warranty to your purchase and secure the longevity of your product. Listed below are some of the most popular options for the product you're purchasing."
              )}
            </p>
          )}
          {richRelevanceSSEComplete && renderManufactorWarranty()}
          {richRelevanceSSEComplete && render3rdPartyWarranty()}
        </Modal.Body>
        <Modal.Footer>
          <ButtonGroup align="right">
            <Button
              size="small"
              color="link"
              onClick={() => switchModal()}
              data-testid="denyWarrantyPurchase"
              label={t('I do not want to protect my purchase')}
              aria-label={t('I do not want to protect my purchase')}
            >
              {t('I do not want to protect my purchase')}
            </Button>
            <Button
              size="small"
              color="primary"
              onClick={() => addItemToCart()}
              isLoading={addToCartState.loading}
              isDisabled={
                selectedWarranty?.callForPrice ||
                !selectedWarranty?.hasAvilablePrice ||
                !richRelevanceSSEComplete
              }
              data-testid="addProtection"
              label={t('Add protection')}
              aria-label={t('Add protection')}
            >
              {t('Add protection')}
            </Button>
          </ButtonGroup>
        </Modal.Footer>
      </div>
    </Modal>
  );
};

export default AddToCartWarrantyModal;

AddToCartWarrantyModal.propTypes = {
  protection: PropTypes.object,
  thirdPartyProtection: PropTypes.object,
  products: PropTypes.array,
  addToCart: PropTypes.func,
  setCurrentModal: PropTypes.func,
  selectedWarranty: PropTypes.object,
  setSelectedWarranty: PropTypes.func,
  showPrice: PropTypes.bool,
  showVAT: PropTypes.bool,
};
